import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Star, MessageCircle, ThumbsUp, User, Calendar, 
  RefreshCw, Filter, Reply, Trash2, Eye, EyeOff,
  Award, TrendingUp, ChevronDown, Search, Car, Package, ShoppingBag, Shirt
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import api from '../../services/api';

const SERVICE_ICONS = {
  rides: Car,
  courier: Package,
  errands: ShoppingBag,
  laundry: Shirt
};

const CustomerReviewsTab = () => {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, 5, 4, 3, 2, 1, needs_response
  const [searchTerm, setSearchTerm] = useState('');
  const [respondingTo, setRespondingTo] = useState(null);
  const [responseText, setResponseText] = useState('');

  useEffect(() => {
    fetchData();
  }, [filter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [reviewsRes, statsRes] = await Promise.all([
        api.get('/reviews/'),
        api.get('/reviews/stats')
      ]);
      
      let reviewsData = Array.isArray(reviewsRes.data) ? reviewsRes.data : [];
      
      // Apply filters
      if (filter !== 'all' && filter !== 'needs_response') {
        reviewsData = reviewsData.filter(r => r.rating === parseInt(filter));
      }
      if (filter === 'needs_response') {
        reviewsData = reviewsData.filter(r => !r.owner_response);
      }
      
      setReviews(reviewsData);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
      toast.error('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  const handleRespond = async (reviewId) => {
    if (!responseText.trim()) {
      toast.error('Please enter a response');
      return;
    }

    try {
      await api.post(`/reviews/${reviewId}/respond`, { response: responseText });
      toast.success('Response added');
      setRespondingTo(null);
      setResponseText('');
      fetchData();
    } catch (error) {
      toast.error('Failed to add response');
    }
  };

  const handleHideReview = async (reviewId) => {
    if (!confirm('Hide this review? It will no longer appear publicly.')) return;
    
    try {
      await api.delete(`/reviews/${reviewId}`);
      toast.success('Review hidden');
      fetchData();
    } catch (error) {
      toast.error('Failed to hide review');
    }
  };

  const handleFeature = async (reviewId) => {
    try {
      await api.post(`/reviews/${reviewId}/feature`);
      toast.success('Review featured on homepage');
      fetchData();
    } catch (error) {
      toast.error('Failed to feature review');
    }
  };

  const StarRating = ({ rating, size = 'sm' }) => {
    const sizeClass = size === 'lg' ? 'w-6 h-6' : size === 'md' ? 'w-5 h-5' : 'w-4 h-4';
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map(star => (
          <Star
            key={star}
            className={`${sizeClass} ${star <= rating ? 'fill-[#D4AF37] text-[#D4AF37]' : 'text-white/20'}`}
          />
        ))}
      </div>
    );
  };

  const ServiceIcon = ({ type }) => {
    const Icon = SERVICE_ICONS[type] || Package;
    return <Icon className="w-4 h-4" />;
  };

  const filteredReviews = reviews.filter(r => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      r.customer_name?.toLowerCase().includes(term) ||
      r.comment?.toLowerCase().includes(term) ||
      r.worker_name?.toLowerCase().includes(term) ||
      r.service_name?.toLowerCase().includes(term)
    );
  });

  const needsResponseCount = reviews.filter(r => !r.owner_response).length;

  return (
    <div className="space-y-6" data-testid="customer-reviews-tab">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold font-['Cormorant_Garamond'] flex items-center gap-2">
            <Star className="w-6 h-6 text-[#D4AF37]" />
            Customer Reviews
          </h2>
          <p className="text-[var(--text-muted)] text-sm mt-1">
            Manage customer feedback and ratings
          </p>
        </div>
        <Button variant="outline" onClick={fetchData}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="card p-4 text-center">
            <div className="flex items-center justify-center gap-1 mb-2">
              <Star className="w-8 h-8 fill-[#D4AF37] text-[#D4AF37]" />
              <span className="text-3xl font-bold text-[#D4AF37]">{stats.average_rating || 0}</span>
            </div>
            <p className="text-xs text-[var(--text-muted)]">Average Rating</p>
          </div>
          <div className="card p-4 text-center">
            <p className="text-3xl font-bold text-[var(--primary)]">{stats.total_reviews || 0}</p>
            <p className="text-xs text-[var(--text-muted)]">Total Reviews</p>
          </div>
          <div className="card p-4 text-center">
            <p className="text-3xl font-bold text-green-500">{stats.recommendation_rate || 0}%</p>
            <p className="text-xs text-[var(--text-muted)]">Would Recommend</p>
          </div>
          <div className="card p-4 text-center">
            <p className="text-3xl font-bold text-purple-500">{stats.rating_breakdown?.[5] || 0}</p>
            <p className="text-xs text-[var(--text-muted)]">5-Star Reviews</p>
          </div>
          <div className="card p-4 text-center border-l-4 border-yellow-400">
            <p className="text-3xl font-bold text-yellow-500">{needsResponseCount}</p>
            <p className="text-xs text-[var(--text-muted)]">Needs Response</p>
          </div>
        </div>
      )}

      {/* Rating Breakdown */}
      {stats?.rating_breakdown && (
        <div className="card p-4">
          <h3 className="font-semibold mb-4">Rating Breakdown</h3>
          <div className="space-y-2">
            {[5, 4, 3, 2, 1].map(rating => {
              const count = stats.rating_breakdown[rating] || 0;
              const percentage = stats.total_reviews > 0 
                ? Math.round((count / stats.total_reviews) * 100) 
                : 0;
              return (
                <div key={rating} className="flex items-center gap-3">
                  <div className="flex items-center gap-1 w-20">
                    <span className="text-sm">{rating}</span>
                    <Star className="w-4 h-4 fill-[#D4AF37] text-[#D4AF37]" />
                  </div>
                  <div className="flex-1 h-3 bg-[var(--background-subtle)] rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${percentage}%` }}
                      className="h-full bg-[#D4AF37] rounded-full"
                    />
                  </div>
                  <span className="text-sm text-[var(--text-muted)] w-12">{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-muted)]" />
          <Input
            placeholder="Search reviews..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-40">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filter" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Reviews</SelectItem>
            <SelectItem value="needs_response">Needs Response</SelectItem>
            <SelectItem value="5">5 Stars</SelectItem>
            <SelectItem value="4">4 Stars</SelectItem>
            <SelectItem value="3">3 Stars</SelectItem>
            <SelectItem value="2">2 Stars</SelectItem>
            <SelectItem value="1">1 Star</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Reviews List */}
      {loading ? (
        <div className="text-center py-12">
          <RefreshCw className="w-8 h-8 text-[var(--primary)] animate-spin mx-auto mb-4" />
          <p className="text-[var(--text-muted)]">Loading reviews...</p>
        </div>
      ) : filteredReviews.length === 0 ? (
        <div className="card p-12 text-center">
          <Star className="w-16 h-16 text-[var(--text-muted)] mx-auto mb-4 opacity-30" />
          <h3 className="text-lg font-semibold mb-2">No Reviews Yet</h3>
          <p className="text-[var(--text-muted)]">
            Customer reviews will appear here after they complete services
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredReviews.map((review) => (
            <motion.div
              key={review.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="card p-5"
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[var(--primary)]/20 flex items-center justify-center">
                    <User className="w-5 h-5 text-[var(--primary)]" />
                  </div>
                  <div>
                    <p className="font-medium">{review.customer_name || 'Anonymous'}</p>
                    <div className="flex items-center gap-2 text-xs text-[var(--text-muted)]">
                      <ServiceIcon type={review.service_type} />
                      <span>{review.service_name || review.service_type}</span>
                      {review.worker_name && (
                        <>
                          <span>•</span>
                          <span>with {review.worker_name}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <StarRating rating={review.rating} />
                  <p className="text-xs text-[var(--text-muted)] mt-1">
                    {new Date(review.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>

              {/* Comment */}
              {review.comment && (
                <p className="text-[var(--text-secondary)] mb-4 bg-[var(--background-subtle)] p-3 rounded-lg">
                  "{review.comment}"
                </p>
              )}

              {/* Sub-ratings */}
              {(review.service_quality || review.punctuality || review.communication) && (
                <div className="flex flex-wrap gap-4 mb-4 text-sm">
                  {review.service_quality && (
                    <div className="flex items-center gap-1">
                      <span className="text-[var(--text-muted)]">Quality:</span>
                      <StarRating rating={review.service_quality} size="sm" />
                    </div>
                  )}
                  {review.punctuality && (
                    <div className="flex items-center gap-1">
                      <span className="text-[var(--text-muted)]">Punctuality:</span>
                      <StarRating rating={review.punctuality} size="sm" />
                    </div>
                  )}
                  {review.communication && (
                    <div className="flex items-center gap-1">
                      <span className="text-[var(--text-muted)]">Communication:</span>
                      <StarRating rating={review.communication} size="sm" />
                    </div>
                  )}
                </div>
              )}

              {/* Would Recommend */}
              {review.would_recommend && (
                <div className="flex items-center gap-2 mb-4">
                  <ThumbsUp className="w-4 h-4 text-green-500" />
                  <span className="text-sm text-green-500">Would recommend</span>
                </div>
              )}

              {/* Owner Response */}
              {review.owner_response && (
                <div className="bg-[var(--primary)]/10 border border-[var(--primary)]/30 rounded-lg p-4 mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Reply className="w-4 h-4 text-[var(--primary)]" />
                    <span className="text-sm font-medium text-[var(--primary)]">Owner Response</span>
                    <span className="text-xs text-[var(--text-muted)]">
                      {review.owner_response_at && new Date(review.owner_response_at).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-sm">{review.owner_response}</p>
                </div>
              )}

              {/* Response Form */}
              <AnimatePresence>
                {respondingTo === review.id && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="mb-4 overflow-hidden"
                  >
                    <Textarea
                      placeholder="Write your response..."
                      value={responseText}
                      onChange={(e) => setResponseText(e.target.value)}
                      className="mb-2"
                      rows={3}
                    />
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        onClick={() => handleRespond(review.id)}
                        className="bg-[var(--primary)] text-black"
                      >
                        Send Response
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => { setRespondingTo(null); setResponseText(''); }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Actions */}
              <div className="flex gap-2 pt-3 border-t border-[var(--border)]">
                {!review.owner_response && (
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => setRespondingTo(review.id)}
                  >
                    <Reply className="w-4 h-4 mr-1" />
                    Respond
                  </Button>
                )}
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => handleFeature(review.id)}
                  className="text-[var(--primary)] border-[var(--primary)]"
                >
                  <Award className="w-4 h-4 mr-1" />
                  Feature
                </Button>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => handleHideReview(review.id)}
                  className="text-red-500 border-red-500/50 hover:bg-red-500/10"
                >
                  <EyeOff className="w-4 h-4 mr-1" />
                  Hide
                </Button>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CustomerReviewsTab;
